import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useWhatsApp } from '../context/WhatsAppContext';

const STATUS_DURATION = 5000; // 5 seconds per status

const StatusViewer = ({ statusId, onClose }) => {
  const { statuses, myStatus } = useWhatsApp();

  // Determine the media array (same as before)
  let media = [];
  if (statusId === 'my-status') {
    media = myStatus?.media || [];
  } else {
    const found = statuses.find(s => s.id === statusId);
    media = found?.media || [];
  }

  const [currentIndex, setCurrentIndex] = useState(0);
  const progressRef = useRef(null);
  const timerRef = useRef(null);

  // Reset index whenever the statusId changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [statusId]);

  const goNext = useCallback(() => {
    if (currentIndex < media.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      onClose();
    }
  }, [currentIndex, media.length, onClose]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) setCurrentIndex(prev => prev - 1);
  }, [currentIndex]);

  // Auto‑advance with a single timer, but we'll simulate progress via animation
  useEffect(() => {
    if (!media.length) return;
    // Clear any previous timer
    clearTimeout(timerRef.current);
    // Start a new timer for the current segment
    timerRef.current = setTimeout(goNext, STATUS_DURATION);
    return () => clearTimeout(timerRef.current);
  }, [currentIndex, goNext, media.length]);

  // Handle video ended
  const handleVideoEnded = () => {
    goNext();
  };

  if (!media.length) {
    return (
      <div className="fixed inset-0 z-50 bg-black text-white flex items-center justify-center">
        <p>No status to show</p>
        <button onClick={onClose} className="absolute top-4 right-4 text-white"><X size={30} /></button>
      </div>
    );
  }

  const current = media[currentIndex];

  return (
    <div className="fixed inset-0 z-50 bg-black">
      {/* Close button */}
      <button onClick={onClose} className="absolute top-4 right-4 z-10 text-white"><X size={30} /></button>

      {/* Segmented progress bars */}
      <div className="absolute top-0 left-0 w-full flex gap-1 p-1">
        {media.map((_, idx) => (
          <div key={idx} className="flex-1 h-1 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-100 ease-linear"
              style={{
                width:
                  idx < currentIndex
                    ? '100%'
                    : idx === currentIndex
                    ? '100%' // This will be animated via the next trick
                    : '0%',
                // For the active segment, force a re-render to trigger animation
                animation:
                  idx === currentIndex
                    ? `progress ${STATUS_DURATION}ms linear forwards`
                    : 'none',
              }}
            />
          </div>
        ))}
      </div>

      {/* Navigation arrows */}
      <button
        onClick={goPrev}
        className="absolute left-2 top-1/2 -translate-y-1/2 z-10 text-white opacity-70 hover:opacity-100 disabled:opacity-30"
        disabled={currentIndex === 0}
      >
        <ChevronLeft size={40} />
      </button>
      <button
        onClick={goNext}
        className="absolute right-2 top-1/2 -translate-y-1/2 z-10 text-white opacity-70 hover:opacity-100 disabled:opacity-30"
        disabled={currentIndex === media.length - 1}
      >
        <ChevronRight size={40} />
      </button>

      {/* Content */}
      <div className="w-full h-full flex items-center justify-center p-4">
        {current.type === 'text' ? (
          <div
            className="w-full max-w-md p-6 rounded-xl text-white text-2xl font-semibold text-center"
            style={{ backgroundColor: current.bgColor || '#128C7E' }}
          >
            {current.text}
          </div>
        ) : current.type === 'video' ? (
          <video
            src={current.url || current.src}
            autoPlay
            muted
            className="max-h-full max-w-full object-contain"
            onEnded={handleVideoEnded}
          />
        ) : (
          <img
            src={current.url || current.src}
            alt="status"
            className="max-h-full max-w-full object-contain"
          />
        )}
      </div>

      {/* Caption */}
      {current.caption && (
        <div className="absolute bottom-10 left-0 w-full text-center text-white text-lg px-4">
          {current.caption}
        </div>
      )}

      {/* Inject CSS for progress animation */}
      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default StatusViewer;